import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Smartphone, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const UpiSetup = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [upiId, setUpiId] = useState('');
  const [holderName, setHolderName] = useState('');

  useEffect(() => {
    fetchGroup();
  }, [groupId]);

  const fetchGroup = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}`);
      setGroup(response.data);
      if (response.data.upi_id) {
        setUpiId(response.data.upi_id);
        setHolderName(response.data.bank_holder_name || '');
      }
    } catch (error) {
      toast.error('Failed to fetch group details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!upiId || !holderName) {
      toast.error('Please fill all fields');
      return;
    }
    
    const upiRegex = /^[\w.\-]+@[a-zA-Z]+$/;
    if (!upiRegex.test(upiId)) {
      toast.error('Please enter a valid UPI ID (e.g., 9876543210@paytm)');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${API}/groups/${groupId}/upi?upi_id=${encodeURIComponent(upiId)}&bank_holder_name=${encodeURIComponent(holderName)}&user_id=${user.id}`
      );
      toast.success('UPI ID configured successfully!');
      navigate(`/group/${groupId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update UPI ID');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!group) return null;

  if (group.creator_id !== user.id) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-accent/20 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold font-manrope mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">Only the group creator can set up the payment account.</p>
          <Button onClick={() => navigate(`/group/${groupId}`)} className="rounded-full">
            Back to Group
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <div className="px-4 md:px-8 py-6 border-b border-border">
        <Button
          variant="ghost"
          onClick={() => navigate(`/group/${groupId}`)}
          className="-ml-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Group
        </Button>
      </div>

      <div className="px-4 md:px-8 py-8 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-card border border-border p-8 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-manrope" data-testid="upi-setup-title">
                {group.upi_id ? 'Update' : 'Setup'} Payment Account
              </h1>
              <p className="text-sm text-muted-foreground">Configure where group members will send money</p>
            </div>
          </div>

          {group.upi_id && (
            <div className="bg-accent/50 border border-border rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-primary mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Payment Account Configured</span>
              </div>
              <p className="text-sm text-muted-foreground">You can update your UPI ID below</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="holderName">Account Holder Name</Label>
              <Input
                id="holderName"
                placeholder="Your full name as per bank account"
                value={holderName}
                onChange={(e) => setHolderName(e.target.value)}
                className="rounded-xl mt-2"
                data-testid="holder-name-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                This name will be shown to group members
              </p>
            </div>

            <div>
              <Label htmlFor="upiId">UPI ID</Label>
              <Input
                id="upiId"
                placeholder="yourname@paytm or 9876543210@ybl"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value.toLowerCase())}
                className="rounded-xl mt-2 font-mono"
                data-testid="upi-id-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Your UPI ID from GPay, PhonePe, Paytm, or any UPI app
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <h4 className="font-semibold text-sm mb-2">💡 How to find your UPI ID:</h4>
              <ul className="text-xs space-y-1 text-muted-foreground">
                <li>• Open GPay/PhonePe/Paytm</li>
                <li>• Go to Profile or Settings</li>
                <li>• Look for "UPI ID" or "Payment Address"</li>
                <li>• It looks like: yourname@paytm or mobile@ybl</li>
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full py-6 font-semibold"
              disabled={submitting}
              data-testid="submit-upi-btn"
            >
              {submitting ? 'Saving...' : group.upi_id ? 'Update UPI ID' : 'Save UPI ID'}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default UpiSetup;